import SEO from '@/components/SEO';
import { site } from '@/data/content';
import { Video, ArrowUpRight, Check, MonitorPlay, Shield, Eye, Zap, Users, Clock, Search, User, Copy, ScanLine } from 'lucide-react';

const features = [
  { icon: MonitorPlay, title: 'One-click capture', text: 'Agents request a screen recording from a visitor with a single button. The browser native prompt ensures explicit visitor consent.' },
  { icon: Video, title: 'WebM recordings', text: 'Recordings are captured as standard webm video and uploaded to your workspace storage for review and QA.' },
  { icon: Shield, title: 'Consent first', text: 'The browser handles the permission dialog. No recording starts unless the visitor approves.' },
  { icon: Users, title: 'Support context', text: 'See exactly what the visitor sees so you can resolve bugs, billing issues, and product questions faster.' },
  { icon: Clock, title: '30-day retention', text: 'Recordings follow the same retention rules as other workspace data and are cleaned up automatically.' },
  { icon: Zap, title: 'No extra software', text: 'Visitors do not need to install anything. It works through the same tracker script already on your site.' },
];

const useCases = [
  { title: 'Technical support', text: 'Ask a visitor to share their screen when they report a UI bug or checkout error. You see the issue immediately.' },
  { title: 'Sales demos', text: 'Guide prospects through your product while seeing their screen and pointing them to the right features.' },
  { title: 'Customer onboarding', text: 'Walk new users through setup and catch the exact step where they get stuck.' },
];

const faqs = [
  { q: 'How does ZuriHub Screen Recording work?', a: 'An authorized agent requests a recording from the visitor browser. The visitor sees a native choose-what-to-share prompt and must allow the recording before it begins.' },
  { q: 'What format are recordings in?', a: 'Recordings are captured as WebM video, a widely supported web format that plays in most browsers without extra software.' },
  { q: 'Do visitors need to install anything?', a: 'No. The recorder works through the existing ZuriHub tracker script and the browser built-in getDisplayMedia API.' },
  { q: 'How long are screen recordings stored?', a: 'Recordings are stored for up to 30 days and follow the same retention and redaction rules as other workspace data.' },
  { q: 'Is this compliant with privacy laws?', a: 'You are responsible for disclosing screen recording in your privacy notice and obtaining any required consent. ZuriHub only starts a recording after the visitor explicitly allows it through the browser prompt.' },
];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'ZuriHub Screen Recording',
    description: 'Capture visitor screens with explicit browser consent for support, sales, and onboarding.',
    brand: { '@type': 'Brand', name: 'ZuriHub' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
];

export default function ScreenRecording() {
  return (
    <>
      <SEO
        title="Screen Recording"
        description="ZuriHub Screen Recording lets support and sales teams capture visitor screens with explicit consent for support, demos, and onboarding."
        path="/screen-recording"
        jsonLd={jsonLd}
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
            <MonitorPlay className="h-4 w-4" /> Addon
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Screen Recording <span className="gradient-text">with consent</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            See what your visitor sees. Request a screen capture, get explicit browser consent, and resolve support and sales issues in minutes.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
          >
            Enable recording <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">What Screen Recording does</h2>
            <p className="section-subtitle mx-auto mt-4">Resolve faster when you can see the visitor screen.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="section-title">How it works</h2>
              <p className="mt-4 text-lg text-slate-600">
                Screen Recording is an optional workspace add-on. Once enabled, an authorized agent can request a recording from any active visitor on a page where the ZuriHub tracker is installed.
              </p>
              <ol className="mt-8 space-y-4">
                {[
                  'Enable the Screen Recording add-on in workspace settings.',
                  'An agent clicks request recording for the active visitor.',
                  'The visitor browser shows the native choose-what-to-share dialog.',
                  'The recording is uploaded to the workspace and appears in the activity panel.',
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">{i + 1}</span>
                    <span className="text-slate-700">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-600">
                  <Eye className="h-5 w-5" />
                </div>
                <span className="font-bold">Visitor screen</span>
              </div>
              <div className="mt-6 rounded-2xl bg-slate-800 p-4 text-sm text-slate-300">
                <p className="font-semibold text-white">example.com wants to share the contents of your screen.</p>
                <p className="mt-1">Select what to share and click Allow to start recording.</p>
                <div className="mt-4 flex gap-3">
                  <span className="rounded-lg bg-slate-700 px-3 py-1.5">Cancel</span>
                  <span className="rounded-lg bg-brand-600 px-3 py-1.5">Allow</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-24" aria-labelledby="ids-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="ids-heading" className="section-title">How to get Visitor ID and Session ID</h2>
              <p className="mt-4 text-lg text-slate-600">
                To request a recording for a specific visitor, the agent needs the visitor ID and session ID. The tracker creates these automatically and they are visible in the workspace.
              </p>
              <ol className="mt-8 space-y-5">
                {[
                  { icon: Search, text: 'Open the Live Visitors panel in your workspace.' },
                  { icon: User, text: 'Find the visitor you want to help. Their Visitor ID is listed next to their profile.' },
                  { icon: Copy, text: 'Click the copy button next to the Visitor ID and Session ID.' },
                  { icon: ScanLine, text: 'Paste both IDs into the Screen Recording request form and send the invite.' },
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                      <s.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-900">Step {i + 1}</span>
                      <p className="text-slate-600">{s.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rounded-3xl border border-slate-100 bg-slate-50 p-8 shadow-xl">
              <h3 className="text-lg font-bold text-slate-900">Live visitor card</h3>
              <p className="text-sm text-slate-500">The IDs appear next to every active visitor in the workspace.</p>
              <div className="mt-6 space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">Visitor ID</span>
                    <button type="button" className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 hover:bg-slate-200">
                      <Copy className="h-3.5 w-3.5" /> Copy
                    </button>
                  </div>
                  <code className="mt-2 block rounded-lg bg-slate-900 px-3 py-2 text-sm text-green-400">vis_7h9f...2k1</code>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">Session ID</span>
                    <button type="button" className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 hover:bg-slate-200">
                      <Copy className="h-3.5 w-3.5" /> Copy
                    </button>
                  </div>
                  <code className="mt-2 block rounded-lg bg-slate-900 px-3 py-2 text-sm text-green-400">ses_3m2p...8z4</code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="scale-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="scale-heading" className="section-title">Scalability and retention</h2>
              <p className="mt-4 text-lg text-slate-600">
                Recordings are stored in your workspace R2 bucket and linked to the workspace account. They are not kept permanently. Retention follows your workspace settings and is capped at 30 days unless the owner configures a shorter period.
              </p>
              <ul className="mt-8 space-y-3">
                {['No extra visitor software', 'Consent required every time', 'WebM format for universal playback', 'Indexed by visitor and session', 'Automatic cleanup after 30 days'].map((s) => (
                  <li key={s} className="flex items-start gap-2 text-slate-700">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" /> {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-8 py-12 text-center text-white shadow-xl">
              <p className="text-sm font-bold uppercase tracking-wider text-brand-100">Screen Recording addon</p>
              <p className="mt-2 text-5xl font-extrabold">Custom</p>
              <p className="mt-1 text-brand-100">per workspace / month</p>
              <p className="mt-4 text-sm text-brand-100">Includes recordings, consent flow, and 30-day storage. Contact sales for pricing.</p>
              <a
                href={site.workspaceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
              >
                Start using it <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="use-cases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="use-cases-heading" className="section-title">Use cases</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Teams use screen recording to remove the guesswork from support and sales.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-100 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Frequently asked questions</h2>
          <p className="section-subtitle mx-auto mt-4 text-center max-w-2xl">
            Common questions about ZuriHub Screen Recording.
          </p>
          <div className="mt-10 space-y-4">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="font-semibold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-slate-900 px-6 py-12 text-center text-white sm:px-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">See what your visitors see</h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">
            Enable Screen Recording in your workspace and resolve support and sales issues with full visual context.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Enable Screen Recording <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
